/**
 * Utilidades para el manejo de fechas
 */

/**
 * Función para obtener la fecha actual con el tiempo actual
 * @returns {Date} La fecha y hora actual
 */
export function getCurrentDateTime(): Date {
  return new Date();
}

/**
 * Función para obtener la fecha de inicio del día civil local
 * @param {Date} date - La fecha base
 * @returns {Date} La fecha de inicio del día (00:00:00.000 local)
 */
export function getStartOfDay(date: Date): Date {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  return startOfDay;
}

/**
 * Función para obtener la fecha de fin del día civil local
 * @param {Date} date - La fecha base
 * @returns {Date} La fecha de fin del día (23:59:59.999 local)
 */
export function getEndOfDay(date: Date): Date {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay;
}

/**
 * Función para obtener el último día del mes
 * @param {number} year - El año
 * @param {number} month - El mes
 * @returns {number} El último día del mes
 */
export function getLastDayOfMonth(year: number, month: number): Date {
  // month: 0 = January, 1 = February, ..., 11 = December
  return new Date(year, month + 1, 0);
}

/**
 * Función para obtener el primer día del mes
 * @param {number} year - El año
 * @param {number} month - El mes
 * @returns {Date} El primer día del mes
 */
export function getFirstDayOfMonth(year: number, month: number): Date {
  // month: 0 = January, 1 = February, ..., 11 = December
  return new Date(year, month, 1);
}

/**
 * Función para verificar si una fecha es hoy
 * @param {Date} date - La fecha a verificar
 * @returns {boolean} True si la fecha es hoy
 */
export function isToday(date: Date): boolean {
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

/**
 * Función para obtener la fecha de hace N días
 * @param {number} days - Número de días hacia atrás
 * @returns {Date} La fecha de hace N días
 */
export function getDateDaysAgo(days: number): Date {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
}

/**
 * Función para obtener el primer día del mes actual
 * @returns {Date} El primer día del mes actual
 */
export function getFirstDayOfCurrentMonth(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

/**
 * Función para obtener la fecha de hace N meses
 * @param {number} months - Número de meses hacia atrás
 * @returns {Date} La fecha de hace N meses
 */
export function getDateMonthsAgo(months: number): Date {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date;
}

/**
 * Formatea una fecha como YYYY-MM-DD del calendario local (no UTC).
 * Evita el desplazamiento de un día que produce toISOString() en zonas UTC-.
 */
export function formatDateForInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Función para obtener la fecha actual formateada para inputs
 * @returns {string} La fecha actual formateada como YYYY-MM-DD
 */
export function getCurrentDateFormatted(): string {
  return formatDateForInput(getCurrentDateTime());
}
